import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { DropoffAddressDto } from "@deliveryapp/shared";

import { ApiError, fetchDispatcherCustomer, type DispatcherCustomerMemory } from "../api/client";
import { useAddressSheet } from "../components/AddressSheetProvider";
import { useBootstrap } from "../components/BootstrapProvider";
import { cartLinesFromRemembered, useCart } from "../components/CartProvider";
import { useToast } from "../components/ToastProvider";
import { Button } from "../components/ui";
import {
  DISPATCHER_CHANGED_EVENT,
  dispatcherSessionExpired,
  markDispatcherSessionRejected,
  readDispatcherCall,
  readDispatcherSession,
  writeDispatcherCall,
  writeDispatcherSession,
  type DispatcherCall,
  type DispatcherSession,
} from "./dispatcher-session";

const ACTIVE_STATUS: Record<string, string> = {
  committed: "confirmată",
  awaiting_assignment: "așteaptă curier",
  assigned: "cu curier alocat",
  in_kitchen: "în bucătărie",
  ready: "gata de ridicare",
  picked_up: "ridicată de curier",
  in_transit: "pe drum",
};

function phoneDigits(phone: string): string {
  return phone.replace(/\D/g, "");
}

function ago(iso: string | null): string {
  if (!iso) return "";
  const days = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000);
  if (!Number.isFinite(days)) return "";
  if (days <= 0) return "azi";
  if (days === 1) return "ieri";
  if (days < 30) return `acum ${days} zile`;
  const months = Math.floor(days / 30);
  return months <= 1 ? "acum o lună" : `acum ${months} luni`;
}

function money(minor: number): string {
  return `${(minor / 100).toFixed(2)} lei`;
}

function addressLine(address: DispatcherCustomerMemory["addresses"][number]): string {
  const street = [address.street, address.streetNumber].filter(Boolean).join(" ");
  const details = [
    address.building ? `bl. ${address.building}` : null,
    address.floor ? `et. ${address.floor}` : null,
    address.apartment ? `ap. ${address.apartment}` : null,
  ].filter(Boolean).join(", ");
  return [street, details, address.city].filter((part) => part && part.length > 0).join(", ");
}

/** Abonare la sesiunea/apelul dispecerului (scrise si de pagina de intrare, si de checkout). */
export function useDispatcherState(marketKey: string): { session: DispatcherSession | null; call: DispatcherCall | null } {
  const [state, setState] = useState(() => ({ session: readDispatcherSession(marketKey), call: readDispatcherCall(marketKey) }));
  useEffect(() => {
    const refresh = () => setState({ session: readDispatcherSession(marketKey), call: readDispatcherCall(marketKey) });
    window.addEventListener(DISPATCHER_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(DISPATCHER_CHANGED_EVENT, refresh);
  }, [marketKey]);
  return state;
}

/**
 * Bara dispecerului, deasupra site-ului: apelul curent, clientul recunoscut
 * (adrese, comenzi din TOATE restaurantele pietei) si un click pentru adresa sau
 * pentru „Repetă". Restul — vitrina, coșul, checkout-ul — e site-ul pieței,
 * neschimbat: rutarea e exact cea a unui client.
 */
export function DispatcherBar() {
  const { marketKey, bootstrap } = useBootstrap();
  const { session, call } = useDispatcherState(marketKey);
  const { session: cart, setAddress, selectStorefront, restoreOrder, resetSession } = useCart();
  const addressSheet = useAddressSheet();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [phoneInput, setPhoneInput] = useState(call?.phone ?? "");
  const [memory, setMemory] = useState<DispatcherCustomerMemory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setPhoneInput(call?.phone ?? "");
  }, [call?.phone]);

  const lookupPhone = call?.phone ?? "";
  useEffect(() => {
    if (session === null || phoneDigits(lookupPhone).length < 9) {
      setMemory(null);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchDispatcherCustomer(marketKey, lookupPhone, controller.signal)
      .then((result) => setMemory(result))
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setMemory(null);
        setError(caught instanceof ApiError ? caught.message : "Nu am putut citi clientul.");
        if (caught instanceof ApiError && (caught.status === 401 || caught.status === 403)) {
          // Sesiune expirata sau firma scoasa din dispecerat: fila ramane in modul
          // dispecer, BLOCATA, pana se redeschide din POS (nu devine site public).
          markDispatcherSessionRejected(marketKey);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [marketKey, session, lookupPhone]);

  const onlyStorefront = bootstrap.storefronts.length === 1 ? bootstrap.storefronts[0] : null;

  const goToMenu = useCallback(() => {
    if (onlyStorefront !== undefined && onlyStorefront !== null) {
      selectStorefront(onlyStorefront.slug, onlyStorefront.name);
      navigate(`/vitrina/${encodeURIComponent(onlyStorefront.slug)}`);
    } else {
      navigate("/");
    }
  }, [navigate, onlyStorefront, selectStorefront]);

  const applyAddress = useCallback(
    (address: DispatcherCustomerMemory["addresses"][number], thenMenu: boolean): boolean => {
      if (address.lat === null || address.lng === null) {
        toast({ key: "dispatcher-address", title: "Adresa nu are punct pe hartă", description: "Confirm-o o dată pe hartă; rutarea are nevoie de punct.", tone: "warning" });
        addressSheet.open({ onSaved: thenMenu ? goToMenu : undefined });
        return false;
      }
      const dto: DropoffAddressDto = {
        lat: address.lat,
        lng: address.lng,
        street: address.street,
        streetNumber: address.streetNumber ?? undefined,
        building: address.building ?? undefined,
        floor: address.floor ?? undefined,
        apartment: address.apartment ?? undefined,
        doorCode: address.doorCode ?? undefined,
        city: address.city,
        postalCode: address.postalCode ?? undefined,
        notes: address.notes ?? undefined,
      };
      setAddress(dto);
      if (thenMenu) goToMenu();
      return true;
    },
    [addressSheet, goToMenu, setAddress, toast],
  );

  const lastOrder = useMemo(() => memory?.orders.find((order) => order.items.length > 0) ?? null, [memory]);

  const repeatLastOrder = useCallback(() => {
    if (lastOrder === null) return;
    const storefrontSlug = lastOrder.storefrontSlug ?? onlyStorefront?.slug ?? null;
    const storefront = bootstrap.storefronts.find((item) => item.slug === storefrontSlug) ?? null;
    if (storefrontSlug === null || storefront === null) {
      toast({ key: "dispatcher-repeat", title: "Vitrina ultimei comenzi nu mai e pe site", tone: "warning" });
      return;
    }
    const repeatable = lastOrder.items.filter((item) => item.repeatable && item.offerId !== null);
    const skipped = lastOrder.items.filter((item) => !item.repeatable || item.offerId === null).map((item) => item.name);
    if (repeatable.length === 0) {
      toast({ key: "dispatcher-repeat", title: "Produsele ultimei comenzi nu se mai pot pune singure", description: "Adaugă-le din meniu.", tone: "warning" });
      return;
    }
    if (cart.address === null && memory?.addresses[0]) {
      if (!applyAddress(memory.addresses[0], false)) return;
    }
    restoreOrder(
      storefrontSlug,
      storefront.name,
      cartLinesFromRemembered(
        repeatable.map((item) => ({
          lineId: item.lineId,
          offerId: item.offerId as string,
          skuId: item.skuId,
          name: item.name,
          qty: item.qty,
          unitPriceGross: item.unitPriceGross,
          selectedOptions: item.selectedOptions,
          optionLabels: item.optionLabels,
          imageUrl: null,
        })),
      ),
    );
    toast({
      key: "dispatcher-repeat",
      title: "Ultima comandă e în coș",
      description: skipped.length > 0
        ? `Se verifică la adresă, la prețurile de azi. Întreabă clientul de: ${skipped.join(", ")}.`
        : "Se verifică la adresă, la prețurile de azi.",
      tone: skipped.length > 0 ? "warning" : "success",
      durationMs: skipped.length > 0 ? 8000 : 3500,
    });
    navigate("/cos");
  }, [applyAddress, bootstrap.storefronts, cart.address, lastOrder, memory, navigate, onlyStorefront, restoreOrder, toast]);

  const startCall = (phone: string, name: string | null) => {
    // Apel nou = client nou: cosul si adresa apelantului anterior nu au voie sa ramana.
    resetSession();
    writeDispatcherCall(marketKey, { phone, name });
    setExpanded(true);
    navigate("/");
  };

  if (session === null) return null;
  const placed = call?.lastPlacedCode ?? null;

  return (
    <div className="relative z-30 border-b-2 border-brand bg-brand-soft text-ink shadow-sm" data-testid="dispatcher-bar">
      <div className="mx-auto flex max-w-6xl flex-col gap-2 px-3 py-2 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-brand px-2.5 py-0.5 text-xs font-bold uppercase tracking-wide text-brand-contrast">
            Dispecer
          </span>
          <span className="text-sm font-semibold">{session.operator}</span>
          <form
            className="flex flex-1 items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              const phone = phoneInput.trim();
              if (phoneDigits(phone).length >= 9) startCall(phone, null);
            }}
          >
            <input
              type="tel"
              inputMode="tel"
              className="min-h-9 w-full max-w-[14rem] rounded-lg border border-line bg-surface px-3 text-sm tabular-nums"
              placeholder="Telefonul clientului"
              value={phoneInput}
              onChange={(event) => setPhoneInput(event.target.value)}
              aria-label="Telefonul clientului care sună"
              data-testid="dispatcher-phone"
            />
            <Button type="submit" size="sm" variant="secondary" disabled={phoneDigits(phoneInput).length < 9 || phoneInput.trim() === call?.phone}>
              Caută
            </Button>
          </form>
          <Button size="sm" variant="ghost" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>
            {expanded ? "Ascunde" : "Client"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              resetSession();
              writeDispatcherSession(marketKey, null);
              navigate("/");
            }}
          >
            Ieși
          </Button>
        </div>

        {dispatcherSessionExpired(marketKey) ? (
          <div className="rounded-lg border border-danger px-3 py-2 text-sm text-danger" role="alert">
            Sesiunea de dispecer a expirat. Deschide din nou „Comandă pe piață” din POS sau din Symbai Staff; până atunci comenzile nu pleacă.
          </div>
        ) : null}

        {placed !== null ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-success bg-surface px-3 py-2 text-sm" role="status">
            <span>
              <strong>{`Comanda ${placed} a fost trimisă`}</strong>
              {call?.lastPlacedSeller ? ` la ${call.lastPlacedSeller}` : ""}
              {". Spune-i clientului codul comenzii."}
            </span>
            <Button size="sm" onClick={() => startCall("", null)}>Apel nou</Button>
          </div>
        ) : null}

        {expanded && call !== null && phoneDigits(call.phone).length >= 9 ? (
          <div className="flex flex-col gap-2 rounded-lg bg-surface px-3 py-2 text-sm" data-testid="dispatcher-customer">
            {loading ? <span className="text-ink-muted">Caut clientul…</span> : null}
            {error !== null ? <span className="text-danger">{error}</span> : null}
            {!loading && memory !== null && !memory.known ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  <strong>Client nou</strong>
                  {" — se salvează la prima comandă. Cere adresa și caut-o pe hartă."}
                </span>
                <Button size="sm" onClick={() => addressSheet.open({ onSaved: goToMenu })}>Adresa clientului</Button>
              </div>
            ) : null}
            {!loading && memory?.known ? (
              <>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <strong className="text-base">{memory.name ?? call.name ?? "Client"}</strong>
                  <span className="text-ink-muted">
                    {memory.ordersCount > 0
                      ? `${memory.ordersCount === 1 ? "o comandă" : `${memory.ordersCount} comenzi`} · ultima ${ago(memory.lastOrderAt)}`
                      : "fără comenzi livrate încă"}
                  </span>
                  {memory.activeOrder !== null ? (
                    <span className="rounded-full border border-warning px-2 py-0.5 text-xs font-semibold text-warning">
                      {`Are comanda ${memory.activeOrder.platformOrderCode} ${ACTIVE_STATUS[memory.activeOrder.status] ?? "în curs"}`}
                      {memory.activeOrder.sellerName ? ` · ${memory.activeOrder.sellerName}` : ""}
                    </span>
                  ) : null}
                </div>
                {memory.addresses.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {memory.addresses.slice(0, 4).map((address) => (
                      <button
                        key={address.key}
                        type="button"
                        className="rounded-lg border border-line px-3 py-1.5 text-left hover:border-brand"
                        onClick={() => applyAddress(address, true)}
                        data-testid={`dispatcher-address-${address.key}`}
                      >
                        <span className="block font-medium">{addressLine(address)}</span>
                        <span className="block text-xs text-ink-muted">
                          {[address.doorCode ? `interfon ${address.doorCode}` : null, address.uses > 1 ? `${address.uses} livrări` : null]
                            .filter(Boolean)
                            .join(" · ") || "Livrează aici"}
                        </span>
                      </button>
                    ))}
                    <button
                      type="button"
                      className="rounded-lg border border-dashed border-line px-3 py-1.5 text-left text-ink-muted hover:border-brand"
                      onClick={() => addressSheet.open({ onSaved: goToMenu })}
                    >
                      Altă adresă
                    </button>
                  </div>
                ) : (
                  <Button size="sm" onClick={() => addressSheet.open({ onSaved: goToMenu })}>Adresa clientului</Button>
                )}
                {lastOrder !== null ? (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-ink-muted">
                      {`Ultima comandă (${lastOrder.platformOrderCode}, ${money(lastOrder.total)}): `}
                      {lastOrder.items.slice(0, 4).map((item) => `${item.qty > 1 ? `${item.qty}× ` : ""}${item.name}`).join(", ")}
                      {lastOrder.items.length > 4 ? "…" : ""}
                    </span>
                    <Button size="sm" onClick={repeatLastOrder} data-testid="dispatcher-repeat">
                      Repetă comanda
                    </Button>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
